import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Pain subsection adapter
 * Handles pain assessment including FLACC scale components (7 fields)
 */
export class PainAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Pain assessment fields
    const painStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS
    )?.at(0)

    const painGoal = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL
    )?.at(0)

    // FLACC Scale components (Face, Legs, Activity, Cry, Consolability)
    const flaccScaleFace = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE
    )?.at(0)

    const flaccScaleLegs = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS
    )?.at(0)

    const flaccScaleActivity = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY
    )?.at(0)

    const flaccScaleCry = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY
    )?.at(0)

    const flaccScaleConsolability = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.PAIN_STATUS]: painStatus,
      [RocFormField.PAIN_GOAL]: painGoal,
      [RocFormField.PAIN_FLACC_SCALE_FACE]: flaccScaleFace,
      [RocFormField.PAIN_FLACC_SCALE_LEGS]: flaccScaleLegs,
      [RocFormField.PAIN_FLACC_SCALE_ACTIVITY]: flaccScaleActivity,
      [RocFormField.PAIN_FLACC_SCALE_CRY]: flaccScaleCry,
      [RocFormField.PAIN_FLACC_SCALE_CONSOLABILITY]: flaccScaleConsolability,
      [RocFormField.PAIN_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.PAIN_STATUS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS,
        value: this.getFormDataField(RocFormField.PAIN_STATUS)
      })
    }

    if (this.formDataFieldExists(RocFormField.PAIN_GOAL)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL,
        value: this.getFormDataField(RocFormField.PAIN_GOAL)
      })
    }

    if (this.formDataFieldExists(RocFormField.PAIN_FLACC_SCALE_FACE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE,
        value: this.getFormDataField(RocFormField.PAIN_FLACC_SCALE_FACE)
      })
    }

    if (this.formDataFieldExists(RocFormField.PAIN_FLACC_SCALE_LEGS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS,
        value: this.getFormDataField(RocFormField.PAIN_FLACC_SCALE_LEGS)
      })
    }

    if (this.formDataFieldExists(RocFormField.PAIN_FLACC_SCALE_ACTIVITY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY,
        value: this.getFormDataField(RocFormField.PAIN_FLACC_SCALE_ACTIVITY)
      })
    }

    if (this.formDataFieldExists(RocFormField.PAIN_FLACC_SCALE_CRY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY,
        value: this.getFormDataField(RocFormField.PAIN_FLACC_SCALE_CRY)
      })
    }

    if (this.formDataFieldExists(RocFormField.PAIN_FLACC_SCALE_CONSOLABILITY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY,
        value: this.getFormDataField(RocFormField.PAIN_FLACC_SCALE_CONSOLABILITY)
      })
    }

    if (this.formDataFieldExists(RocFormField.PAIN_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.PAIN_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
